import React, { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import Button from './shared/Button';
import Spinner from './shared/Spinner';

// Superadmin record-edit modal: manipulate a single reconciliation record's fields and save. The form
// keys are the display fields; on save they map to the lowercase SPR field names the backend whitelists.
// Extracted from TaskConfigurationPage so the MessageLogPage can reuse it.
export const RECORD_STATUS_OPTIONS = ['IN-FLIGHT', 'PROCESSED', 'WARNING', 'ERROR', 'SKIPPED', 'REQUESTED', 'DLQ', 'PENDING', 'SUBMITTED'];
// Scopes a superadmin can request a source record under (maps to the source message type server-side).
//
// KEEP IN STEP WITH THE BACKEND. The server resolves a scope through scopeToMessageType and
// scopeToOhipModule in ui-v2/src/hc/sync/sync/update.js; a scope offered here that is absent there gets
// "No OHIP module for scope X", and a scope present there but missing here simply cannot be asked for.
// CONTACT was the second case — the backend has mapped it to PROFILE all along, so a reservation failing
// on a missing ReservationContact could not be resolved from this screen even though the request would
// have worked.
//
// OTHER and SOURCE are the remaining profile scopes the backend maps. RELATIONSHIP is deliberately NOT
// offered: it maps to its own message type rather than PROFILE and is not a record a person requests by
// source id.
export const REQUEST_SCOPE_OPTIONS = [
    'RESERVATIONS', 'INDIVIDUALS', 'COMPANY', 'AGENT', 'CONTACT', 'GROUP', 'OTHER', 'SOURCE',
    'BLOCK', 'RATECONFIG', 'PACKAGECONFIG', 'EVENT', 'ACTIVITY', 'STAY',
];

function RecordEditModal({ record, onClose, onSave }) {
  const [form, setForm] = useState({
    recordstatus: record.recordStatus || '',
    targetrecordid: record.targetRecordId || '',
    sourcerecordid: record.sourcerecordid || '',
    scopetype: record.scopeType || '',
    current_step: record.stage || '',
    comment: record.comment || '',
    sourcedatapoint1: record.sourceDataPoint1 || '',
    sourcedatapoint3: record.sourceDataPoint3 || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      // Only send changed fields so we never clobber values the editor didn't touch.
      const orig = {
        recordstatus: record.recordStatus || '', targetrecordid: record.targetRecordId || '',
        sourcerecordid: record.sourcerecordid || '', scopetype: record.scopeType || '',
        current_step: record.stage || '', comment: record.comment || '',
        sourcedatapoint1: record.sourceDataPoint1 || '', sourcedatapoint3: record.sourceDataPoint3 || '',
      };
      const fields = {};
      Object.keys(form).forEach((k) => { if (form[k] !== orig[k]) fields[k] = form[k]; });
      if (Object.keys(fields).length === 0) { onClose(); return; }
      await onSave(fields);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save record');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay-above-log" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Record</h2>
          <button className="modal-close" onClick={onClose}><X className="icon-sm" /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div className="error-banner"><AlertCircle className="icon-sm" />{error}</div>
            )}
            <div className="form-group">
              <label className="form-label">Record UUID</label>
              <input type="text" className="form-input" value={record.sprRecordUuid || ''} disabled readOnly />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-input" value={form.recordstatus} onChange={set('recordstatus')}>
                {!RECORD_STATUS_OPTIONS.includes(form.recordstatus) && form.recordstatus && (
                  <option value={form.recordstatus}>{form.recordstatus}</option>
                )}
                {RECORD_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Scope Type</label>
              <input type="text" className="form-input" value={form.scopetype} onChange={set('scopetype')} />
            </div>
            <div className="form-group">
              <label className="form-label">Source Record ID</label>
              <input type="text" className="form-input" value={form.sourcerecordid} onChange={set('sourcerecordid')} />
            </div>
            <div className="form-group">
              <label className="form-label">Target Record ID</label>
              <input type="text" className="form-input" value={form.targetrecordid} onChange={set('targetrecordid')} />
            </div>
            <div className="form-group">
              <label className="form-label">Current Step</label>
              <input type="text" className="form-input" value={form.current_step} onChange={set('current_step')} />
            </div>
            <div className="form-group">
              <label className="form-label">Source Data Point 1</label>
              <input type="text" className="form-input" value={form.sourcedatapoint1} onChange={set('sourcedatapoint1')} />
            </div>
            <div className="form-group">
              <label className="form-label">Source Data Point 3</label>
              <input type="text" className="form-input" value={form.sourcedatapoint3} onChange={set('sourcedatapoint3')} />
            </div>
            <div className="form-group">
              <label className="form-label">Comment</label>
              <textarea className="form-input" rows={4} value={form.comment} onChange={set('comment')} />
            </div>
          </div>
          <div className="modal-footer">
            <Button variant="secondary" onClick={onClose} type="button" disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? (<><Spinner size="sm" className="icon-sm" />Saving...</>) : 'Save Record'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RecordEditModal;
